/**
 * Admin: the fee schedule. Trading commission and flat fee per trade per asset class (set on every
 * instrument in it) and the withdrawal fees per crypto asset (in its coin) and per wire / card payout
 * currency. Admin → Withdrawal limits edits the same withdrawal fees. The CRM reads and
 * changes the same schedule (GET / PUT /api/crm/settings/fees) and hears every change made here
 * through the fees_changed webhook.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { D, FEE_ASSET_CLASSES, fmtDateTime, parseFeeUpdate, PAYOUT_CURRENCIES, STANDARD_FEES, WITHDRAWAL_ASSETS, type FeeSchedule, type FeeUpdate } from '@platform/shared';
import { cx, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';

const input = 'h-9 w-full rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const btn = 'h-8 rounded-md border border-line-strong px-3 text-xs font-semibold text-fg disabled:opacity-50';
const primary = 'h-9 rounded-lg bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50';
const err = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');

type Data = { fees: FeeSchedule; updatedBy: string | null; updatedAt: string | null };
type FiatKey = 'bank_wire' | 'card';
/** Everything as typed text. Trading in percent, flat in USD; empty = leave the class as it is (e.g. mixed rates). */
type Form = { trading: Record<string, string>; trading_flat: Record<string, string>; crypto: Record<string, string>; bank_wire: Record<string, string>; card: Record<string, string> };

const pct = (fraction: string) => D.from(fraction).mul(100).toString();
const toForm = (f: FeeSchedule): Form => ({
  trading: Object.fromEntries(FEE_ASSET_CLASSES.map((c) => [c.id, f.trading[c.id].rate === null ? '' : pct(f.trading[c.id].rate!)])),
  trading_flat: Object.fromEntries(FEE_ASSET_CLASSES.map((c) => [c.id, f.trading_flat[c.id].rate ?? ''])),
  crypto: { ...f.withdrawal.crypto },
  bank_wire: { ...f.withdrawal.bank_wire },
  card: { ...f.withdrawal.card },
});
const PCT_RE = /^\d{1,2}(\.\d{1,4})?$/;

/** The change the form makes against what is saved: only fields that differ. */
function changeOf(f: Form, saved: Form): { ok: true; update: FeeUpdate | null } | { ok: false; error: string } {
  const raw: { trading?: Record<string, string>; trading_flat?: Record<string, string>; withdrawal?: Record<string, Record<string, string>> } = {};
  for (const c of FEE_ASSET_CLASSES) {
    const v = f.trading[c.id]!.trim();
    if (v === '' || v === saved.trading[c.id]) continue;
    if (!PCT_RE.test(v)) return { ok: false, error: `${c.label}: enter a percentage like 1 or 0.05` };
    (raw.trading ??= {})[c.id] = D.from(v).div(100).toString();
  }
  for (const c of FEE_ASSET_CLASSES) {
    const v = f.trading_flat[c.id]!.trim();
    if (v === '' || v === saved.trading_flat[c.id]) continue;
    (raw.trading_flat ??= {})[c.id] = v;
  }
  for (const m of ['crypto', 'bank_wire', 'card'] as const) {
    for (const [k, v] of Object.entries(f[m])) {
      if (v.trim() === saved[m][k]) continue;
      ((raw.withdrawal ??= {})[m] ??= {})[k] = v.trim();
    }
  }
  if (!raw.trading && !raw.trading_flat && !raw.withdrawal) return { ok: true, update: null };
  const p = parseFeeUpdate(raw);
  return p.ok ? { ok: true, update: p.update } : p;
}

function Amount({ label, value, onChange, prefix, suffix, placeholder, hint }: { label: string; value: string; onChange: (v: string) => void; prefix?: string; suffix?: string; placeholder?: string; hint?: ReactNode }) {
  return (
    <label className="flex min-w-0 flex-col gap-1 text-[11px] text-muted">
      {label}
      <span className="relative">
        {prefix && <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-xs text-muted">{prefix}</span>}
        <input inputMode="decimal" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))} className={cx(input, 'num', prefix && 'pl-6', suffix && 'pr-12')} />
        {suffix && <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-xs text-muted">{suffix}</span>}
      </span>
      {hint && <span>{hint}</span>}
    </label>
  );
}

function Section({ title, sub, children }: { title: string; sub: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-line bg-panel p-4">
      <div>
        <h2 className="m-0 text-sm font-semibold">{title}</h2>
        <p className="m-0 mt-0.5 text-xs text-muted">{sub}</p>
      </div>
      {children}
    </section>
  );
}

export function FeesTab() {
  const [data, setData] = useState<Data | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [f, setF] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const reload = useCallback(async () => {
    try {
      const d = await api.get<Data>('/api/admin/fees');
      setData(d);
      setF(toForm(d.fees));
      setLoadErr(null);
    } catch (e) {
      setLoadErr(err(e));
    }
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);
  if (!f || !data) return loadErr ? <p className="text-sm text-down">{loadErr}</p> : <Spinner />;

  const saved = toForm(data.fees);
  const edit = (fn: (cur: Form) => Form) => {
    setMsg(null);
    setF((cur) => fn(cur!));
  };
  const setIn = (part: keyof Form, k: string, v: string) => edit((cur) => ({ ...cur, [part]: { ...cur[part], [k]: v } }));
  const change = changeOf(f, saved);
  const standard: Form = {
    trading: Object.fromEntries(Object.entries(STANDARD_FEES.trading).map(([k, v]) => [k, pct(v!)])),
    trading_flat: { ...STANDARD_FEES.trading_flat } as Record<string, string>,
    crypto: { ...saved.crypto, ...STANDARD_FEES.withdrawal.crypto },
    bank_wire: { ...saved.bank_wire, ...STANDARD_FEES.withdrawal.bank_wire },
    card: { ...saved.card, ...STANDARD_FEES.withdrawal.card },
  };
  const atStandard = JSON.stringify(f) === JSON.stringify(standard);

  const save = async () => {
    if (!change.ok || !change.update) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.put<Data & { instrumentsChanged: number }>('/api/admin/fees', change.update);
      setMsg({ ok: true, text: `Saved${r.instrumentsChanged ? ` (${r.instrumentsChanged} instrument${r.instrumentsChanged === 1 ? '' : 's'} updated)` : ''}. New orders and withdrawal requests use these fees now; open positions pay the new rate when they close (a flat fee is only ever charged on the open). The CRM is notified.` });
      await reload();
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 rounded-xl border border-line bg-panel/95 px-4 py-3 backdrop-blur">
        <div className="min-w-[260px] flex-[1_1_360px] text-xs">
          <b className="text-sm">Fees</b>
          <div className="text-muted">
            Shared with the CRM: a change here or there applies to both.
            {data.updatedAt && ` Last changed ${fmtDateTime(data.updatedAt)}${data.updatedBy ? ` by ${data.updatedBy}` : ''}.`}
          </div>
        </div>
        {msg && <span className={cx('text-xs', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</span>}
        {!change.ok && <span className="text-xs text-down">{change.error}</span>}
        <button type="button" className={btn} disabled={atStandard || busy} onClick={() => edit(() => standard)} title="No commission, a flat $1 per trade on every class, no withdrawal fees">
          Standard preset
        </button>
        <button type="button" className={btn} disabled={JSON.stringify(f) === JSON.stringify(saved) || busy} onClick={() => edit(() => saved)}>
          Discard
        </button>
        <button type="button" className={primary} disabled={!change.ok || !change.update || busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <Section title="Trading commission" sub="Percent of the position's value, charged when it opens and again when it closes. Applies to every instrument in the class; spreads, markup and overnight swap are set per instrument in the Instruments tab.">
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {FEE_ASSET_CLASSES.map((c) => {
            const t = data.fees.trading[c.id];
            const v = f.trading[c.id]!;
            const example = PCT_RE.test(v) ? D.from(1000).mul(v).div(100) : null;
            return (
              <Amount
                key={c.id}
                label={`${c.label} (${t.instruments})`}
                value={v}
                suffix="%"
                placeholder={t.rate === null && t.min !== null ? `${pct(t.min)}–${pct(t.max!)}` : 'No change'}
                onChange={(x) => setIn('trading', c.id, x)}
                hint={
                  t.rate === null && t.min !== null && v === ''
                    ? 'Rates differ per instrument; enter one to set them all'
                    : example === null
                      ? ' '
                      : example.isZero()
                        ? 'Commission-free'
                        : `$1,000 trade: $${example.toFixed(2)} in, $${example.toFixed(2)} out`
                }
              />
            );
          })}
        </div>
      </Section>

      <Section title="Flat fee per trade" sub="USD charged once, when a position opens; closing it costs nothing extra. Applies to every instrument in the class, on top of any commission above. 0 = none.">
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {FEE_ASSET_CLASSES.map((c) => {
            const t = data.fees.trading_flat[c.id];
            return (
              <Amount
                key={c.id}
                label={`${c.label} (${t.instruments})`}
                value={f.trading_flat[c.id]!}
                prefix="$"
                placeholder={t.rate === null && t.min !== null ? `${t.min}–${t.max}` : 'No change'}
                onChange={(x) => setIn('trading_flat', c.id, x)}
                hint={t.rate === null && t.min !== null && f.trading_flat[c.id] === '' ? 'Fees differ per instrument; enter one to set them all' : ' '}
              />
            );
          })}
        </div>
      </Section>

      <Section title="Crypto withdrawals" sub="Network fee in the coin, taken from what the client receives. 0 = free.">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {WITHDRAWAL_ASSETS.map((a) => (
            <Amount key={a.id} label={a.label} value={f.crypto[a.id] ?? ''} suffix={a.coin} onChange={(x) => setIn('crypto', a.id, x)} />
          ))}
        </div>
      </Section>

      {(['bank_wire', 'card'] as FiatKey[]).map((m) => (
        <Section key={m} title={m === 'card' ? 'Card withdrawals' : 'Wire withdrawals'} sub="USD taken from the amount, per payout currency. 0 = free.">
          <div className="grid gap-3 sm:grid-cols-3">
            {PAYOUT_CURRENCIES.map((c) => (
              <Amount key={c} label={`${c} payouts`} value={f[m][c] ?? ''} prefix="$" onChange={(x) => setIn(m, c, x)} />
            ))}
          </div>
        </Section>
      ))}
    </div>
  );
}
